import { Button } from '@factorialco/deprecated-design-system'

import { observer } from 'mobx-react'

import Modal from 'modules/core/components/Modal'
import TextField from 'modules/core/components/TextField'
import { useForm } from 'modules/core/hooks/useForm'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'

import useCreateLevel from 'modules/job_catalog/hooks/useCreateLevel'
import type { Level } from 'modules/job_catalog/hooks/useRoleLevels'
import useUpdateLevel from 'modules/job_catalog/hooks/useUpdateLevel'

interface Props {
  draft?: Level
  roleId: string
  onClose: () => void
  onSuccess?: () => void
}

interface FormValues {
  name: string
}

const DraftLevelModal = ({ roleId, draft: initialDraft, onClose, onSuccess }: Props) => {
  const updateLevel = useUpdateLevel()
  const createLevel = useCreateLevel()

  const title = initialDraft
    ? i18n.t('job_catalog.edit_name')
    : i18n.t('job_catalog.draft_level_modal_title')

  const handleUpdateLevel = async (id: string, name: string) => {
    const result = await updateLevel(id, name)
    const handled = handleResult(result, (errors) => {
      form.setErrors({ name: errors.name })
    })

    return handled
  }

  const handleCreateLevel = async (name: string) => {
    const result = await createLevel(name, roleId)
    const handled = handleResult(result, (errors) => {
      form.setErrors({ name: errors.name })
    })

    return handled
  }

  const form = useForm<FormValues>({
    defaultValues: {
      name: initialDraft?.name || '',
    },
    onSubmit: async ({ name }) => {
      const handled = initialDraft
        ? await handleUpdateLevel(initialDraft.id, name)
        : await handleCreateLevel(name)

      if (handled.success) {
        if (onSuccess) {
          onSuccess()
        }

        onClose()
      }

      return handled
    },
  })

  return (
    <Modal
      illustration="automations"
      title={title}
      description={i18n.t('job_catalog.draft_level_modal_description')}
      footer={
        <Button disabled={!form.dirty} submit>
          {i18n.t('common.submit')}
        </Button>
      }
      onClose={onClose}
      onSubmit={() => form.handleSubmit()}
    >
      <TextField
        mandatory
        label={i18n.t('job_catalog.level')}
        placeholder={i18n.t('job_catalog.job_name')}
        value={form.values.name}
        onChange={(name) => form.setValues({ ...form.values, name })}
      />
    </Modal>
  )
}

export default observer(DraftLevelModal)
